/**
 * State Persistence Utility for KernLogic Session Management
 *
 * Handles persisting in-flight form data and import wizard state
 * across session expiry and login/logout cycles using sessionStorage
 * and IndexedDB fallback for larger data.
 */

import { logError, logInfo } from '@/utils/errorHandler';

// Storage keys for different types of persistent data
const STORAGE_KEYS = {
  FORM_DATA: 'kernlogic_form_state',
  IMPORT_WIZARD: 'kernlogic_import_wizard',
  UPLOAD_PROGRESS: 'kernlogic_upload_progress',
  NAVIGATION_STATE: 'kernlogic_navigation_state',
} as const;

// Types for persisted data
interface FormState {
  formId: string;
  data: Record<string, any>;
  timestamp: number;
  userId?: string;
  route: string;
}

interface ImportWizardState {
  step: number;
  data: Record<string, any>;
  files?: File[];
  uploadProgress?: number;
  timestamp: number;
  userId?: string;
}

interface NavigationState {
  currentRoute: string;
  previousRoute?: string;
  timestamp: number;
}

// Helper to check if IndexedDB is available
function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

// IndexedDB helper for large data
class IndexedDBStorage {
  private dbName = 'KernLogicSessionData';
  private version = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    if (!isIndexedDBAvailable()) {
      throw new Error('IndexedDB not available');
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('formData')) {
          db.createObjectStore('formData', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('importWizard')) {
          db.createObjectStore('importWizard', { keyPath: 'id' });
        }
      };
    });
  }

  async setItem(storeName: string, key: string, value: any): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put({ id: key, data: value, timestamp: Date.now() });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getItem(storeName: string, key: string): Promise<any> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
    });
  }

  async removeItem(storeName: string, key: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
}

const idbStorage = new IndexedDBStorage();

// Utility functions for different storage backends
async function setStorageItem(key: string, value: any, useIndexedDB = false): Promise<void> {
  try {
    const serializedValue = JSON.stringify(value);

    // Use IndexedDB for large data or when explicitly requested
    if (useIndexedDB || serializedValue.length > 1024 * 1024) {
      // 1MB threshold
      await idbStorage.setItem('formData', key, value);
      // Store a reference in sessionStorage
      sessionStorage.setItem(`${key}_idb`, 'true');
    } else {
      sessionStorage.setItem(key, serializedValue);
    }

    logInfo('State persisted successfully', {
      component: 'statePersistence',
      action: 'setStorageItem',
      metadata: { key, size: serializedValue.length, useIndexedDB },
    });
  } catch (error) {
    logError('Failed to persist state', {
      component: 'statePersistence',
      action: 'setStorageItem',
      metadata: { key, error },
    });
    throw error;
  }
}

async function getStorageItem(key: string): Promise<any> {
  try {
    // Check if data is in IndexedDB
    const isInIndexedDB = sessionStorage.getItem(`${key}_idb`) === 'true';

    if (isInIndexedDB) {
      const value = await idbStorage.getItem('formData', key);
      return value;
    } else {
      const serializedValue = sessionStorage.getItem(key);
      return serializedValue ? JSON.parse(serializedValue) : null;
    }
  } catch (error) {
    logError('Failed to retrieve persisted state', {
      component: 'statePersistence',
      action: 'getStorageItem',
      metadata: { key, error },
    });
    return null;
  }
}

async function removeStorageItem(key: string): Promise<void> {
  try {
    const isInIndexedDB = sessionStorage.getItem(`${key}_idb`) === 'true';

    if (isInIndexedDB) {
      await idbStorage.removeItem('formData', key);
      sessionStorage.removeItem(`${key}_idb`);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    logError('Failed to remove persisted state', {
      component: 'statePersistence',
      action: 'removeStorageItem',
      metadata: { key, error },
    });
  }
}

// Public API for form state persistence
export const formStatePersistence = {
  /**
   * Save form state with automatic cleanup after 24 hours
   */
  async saveFormState(formId: string, data: Record<string, any>, route: string): Promise<void> {
    const userId = localStorage.getItem('user_id');
    const formState: FormState = {
      formId,
      data,
      timestamp: Date.now(),
      userId: userId || undefined,
      route,
    };

    const key = `${STORAGE_KEYS.FORM_DATA}_${formId}`;
    await setStorageItem(key, formState);
  },

  /**
   * Retrieve form state by form ID
   */
  async getFormState(formId: string): Promise<FormState | null> {
    const key = `${STORAGE_KEYS.FORM_DATA}_${formId}`;
    const state = await getStorageItem(key);

    if (!state) return null;

    // Check if state is stale (older than 24 hours)
    const isStale = Date.now() - state.timestamp > 24 * 60 * 60 * 1000;
    if (isStale) {
      await this.clearFormState(formId);
      return null;
    }

    return state;
  },

  /**
   * Clear form state
   */
  async clearFormState(formId: string): Promise<void> {
    const key = `${STORAGE_KEYS.FORM_DATA}_${formId}`;
    await removeStorageItem(key);
  },

  /**
   * Clear all form states for current user
   */
  async clearAllFormStates(): Promise<void> {
    try {
      // Clear sessionStorage entries
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(STORAGE_KEYS.FORM_DATA)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => sessionStorage.removeItem(key));

      logInfo('Cleared all form states', {
        component: 'statePersistence',
        action: 'clearAllFormStates',
        metadata: { clearedCount: keysToRemove.length },
      });
    } catch (error) {
      logError('Failed to clear form states', {
        component: 'statePersistence',
        action: 'clearAllFormStates',
        metadata: { error },
      });
    }
  },
};

// Public API for import wizard state persistence
export const importWizardPersistence = {
  /**
   * Save import wizard state
   */
  async saveWizardState(data: Record<string, any>, step: number, files?: File[]): Promise<void> {
    const userId = localStorage.getItem('user_id');
    const wizardState: ImportWizardState = {
      step,
      data,
      files, // Note: Files may not persist across browser sessions
      timestamp: Date.now(),
      userId: userId || undefined,
    };

    // Use IndexedDB for wizard state as it may contain file data
    await setStorageItem(STORAGE_KEYS.IMPORT_WIZARD, wizardState, true);
  },

  /**
   * Retrieve import wizard state
   */
  async getWizardState(): Promise<ImportWizardState | null> {
    const state = await getStorageItem(STORAGE_KEYS.IMPORT_WIZARD);

    if (!state) return null;

    // Check if state is stale (older than 24 hours)
    const isStale = Date.now() - state.timestamp > 24 * 60 * 60 * 1000;
    if (isStale) {
      await this.clearWizardState();
      return null;
    }

    return state;
  },

  /**
   * Clear import wizard state
   */
  async clearWizardState(): Promise<void> {
    await removeStorageItem(STORAGE_KEYS.IMPORT_WIZARD);
  },

  /**
   * Update upload progress
   */
  async updateUploadProgress(progress: number): Promise<void> {
    await setStorageItem(STORAGE_KEYS.UPLOAD_PROGRESS, { progress, timestamp: Date.now() });
  },

  /**
   * Get upload progress
   */
  async getUploadProgress(): Promise<number | null> {
    const state = await getStorageItem(STORAGE_KEYS.UPLOAD_PROGRESS);
    return state?.progress || null;
  },
};

// Public API for navigation state persistence
export const navigationPersistence = {
  /**
   * Save current navigation state
   */
  async saveNavigationState(currentRoute: string, previousRoute?: string): Promise<void> {
    const navState: NavigationState = {
      currentRoute,
      previousRoute,
      timestamp: Date.now(),
    };

    await setStorageItem(STORAGE_KEYS.NAVIGATION_STATE, navState);
  },

  /**
   * Get navigation state
   */
  async getNavigationState(): Promise<NavigationState | null> {
    return await getStorageItem(STORAGE_KEYS.NAVIGATION_STATE);
  },

  /**
   * Clear navigation state
   */
  async clearNavigationState(): Promise<void> {
    await removeStorageItem(STORAGE_KEYS.NAVIGATION_STATE);
  },
};

// Cleanup utility for expired data
export async function cleanupExpiredState(): Promise<void> {
  try {
    const now = Date.now();
    const expiredThreshold = 24 * 60 * 60 * 1000; // 24 hours

    // Clean up sessionStorage
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('kernlogic_')) {
        try {
          const data = sessionStorage.getItem(key);
          if (data) {
            const parsed = JSON.parse(data);
            if (parsed.timestamp && now - parsed.timestamp > expiredThreshold) {
              keysToRemove.push(key);
            }
          }
        } catch {
          // Invalid JSON, remove it
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => sessionStorage.removeItem(key));

    logInfo('Cleaned up expired state', {
      component: 'statePersistence',
      action: 'cleanupExpiredState',
      metadata: { removedCount: keysToRemove.length },
    });
  } catch (error) {
    logError('Failed to cleanup expired state', {
      component: 'statePersistence',
      action: 'cleanupExpiredState',
      metadata: { error },
    });
  }
}

// Initialize cleanup on module load
if (typeof window !== 'undefined') {
  // Clean up expired state on page load
  cleanupExpiredState();
}
